import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { SpreadSheetService } from "./spreadsheet.service";
import Spreadsheet from "./types/x-spreadsheet.types";
import { RowData, Rows, Stox, XLSX } from "./types/sheet.js.types.expanded";
import { ScrollLockService } from "../../services/scroll.lock.service";

@TypeManagerDecorator("s25-ng-spreadsheet")
@Component({
    selector: "s25-ng-spreadsheet",
    template: `
        <s25-ng-file-upload
            [onAdd]="handleFileUpload"
            [readAsArrayBuffer]="true"
            [showFiles]="false"
        ></s25-ng-file-upload>
        @if (onComplete) {
            <s25-ng-button
                [buttonClass]="'ngBlock ngFloatRight c-margin-top--single c-margin-bottom--single'"
                [type]="'primary'"
                [onClick]="handleComplete"
                >{{ completeText }}</s25-ng-button
            >
        }

        @if (uploaded) {
            <div [style.width]="'fit-content'" class="c-margin-top--single" #spreadsheetEl></div>
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SpreadsheetComponent implements OnInit, OnDestroy {
    private static readonly HEIGHT_PER_ROW = 25;
    private static readonly HEADER_HEIGHT = 5;
    private static readonly DEFAULT_ROWS = 25;
    private static readonly DEFAULT_COLS = 26;
    private static readonly MAX_VIEWPORT_HEIGHT = 600; // Maximum viewport height to prevent canvas size errors

    @ViewChild("spreadsheetEl") spreadsheetEl: ElementRef<HTMLDivElement>;

    @Input() onUpload?: (rows: RowData[]) => void;
    @Input() onComplete?: (rows: RowData[]) => Promise<unknown>;
    @Input() completeText?: string = "Done";
    @Input() heightFunc?: () => number;
    @Input() widthFunc: () => number = () => 800;

    spreadsheet: Spreadsheet;
    init = false;
    uploaded = false;
    XLSX: XLSX;
    scrollLocksHandled = false;

    constructor(private cd: ChangeDetectorRef) {}

    handleComplete = () => {
        return (this.onComplete && this.onComplete(this.getSheetData())) || Promise.resolve();
    };

    handleFileUpload = async (file: S25File) => {
        ScrollLockService.unlockBodyScroll();

        // destroy the existing spreadsheet instance if it exists
        this.uploaded = false;
        this.cd.detectChanges();

        // recreate spreadsheet element
        this.uploaded = true;
        this.cd.detectChanges();

        await SpreadSheetService.load();

        const xsprData: Stox = await SpreadSheetService.stox(this.XLSX.read(file.data));
        const rowCount = this.getRowCount(xsprData?.[0]?.rows);
        const calculatedHeight =
            (rowCount || SpreadsheetComponent.DEFAULT_ROWS) * SpreadsheetComponent.HEIGHT_PER_ROW +
            SpreadsheetComponent.HEADER_HEIGHT +
            SpreadsheetComponent.HEIGHT_PER_ROW; // Extra row height to ensure last row is fully visible
        // Cap height at maximum to prevent canvas size errors for large datasets
        const myHeight = Math.min(calculatedHeight, SpreadsheetComponent.MAX_VIEWPORT_HEIGHT);
        const myHeightFunc = this.heightFunc || (() => myHeight);

        this.spreadsheet = window.x_spreadsheet(this.spreadsheetEl.nativeElement, {
            mode: "edit",
            showToolbar: false,
            showGrid: true,
            showContextmenu: true,
            showBottomBar: false,
            view: {
                height: myHeightFunc,
                width: this.widthFunc,
            },
            col: {
                len: this.getColCount(xsprData?.[0]?.rows),
                width: 100,
                indexWidth: 60,
                minWidth: 60,
            },
        });

        this.handleScrollLock();
        this.spreadsheet.loadData(xsprData);
        this.onUpload && this.onUpload(this.getSheetData());
        this.cd.detectChanges();
    };

    getRowCount = (rows: Rows): number => {
        let realRows = 0;
        for (const rowNum in rows || {}) {
            if (!S25Util.isParsableAsInt(rowNum)) {
                continue;
            }
            const cells = rows?.[S25Util.toInt(rowNum)]?.cells || {};
            if (Object.keys(cells || {}).length > 0) {
                for (const cellNum in cells) {
                    const cell = cells[cellNum];
                    if (cell && cell.text && S25Util.toStr(cell.text).trim().length > 0) {
                        realRows++;
                        break; // only count the first non-empty cell in the row
                    }
                }
            }
        }
        return Math.max(
            SpreadsheetComponent.DEFAULT_ROWS,
            realRows + 1, // +1 to account for the header row
        );
    };

    getColCount = (rows: Rows): number => {
        return Math.max(
            SpreadsheetComponent.DEFAULT_COLS,
            ...Object.keys(rows || {})
                .filter(S25Util.isParsableAsInt)
                .map((rowNum: string) => {
                    const cells = rows?.[S25Util.toInt(rowNum)]?.cells || {};
                    return (
                        Math.max(
                            0,
                            ...Object.keys(cells || {})
                                .filter(S25Util.isParsableAsInt)
                                .map(S25Util.toInt),
                        ) + 1
                    );
                }),
        );
    };

    getSheetData = (): RowData[] => {
        if (!this.spreadsheet) {
            return [];
        }
        const rows: RowData[] = [];
        const data = this.spreadsheet.getData();
        if (!data || !data.length || !data[0]) {
            return rows;
        }
        const sheet = data[0];
        const colsNum = sheet?.cols?.len || 0;
        if (sheet?.rows) {
            for (const rowNum in sheet.rows) {
                if (!S25Util.isParsableAsInt(rowNum)) {
                    continue;
                }
                const row = sheet?.rows?.[rowNum];
                if (!row) {
                    continue;
                }
                const rowData: RowData = { cells: [] };
                let realCols = 0;
                let hasNonEmptyCell = false;
                for (let colNum = 0; colNum < colsNum; colNum++) {
                    const cell = row?.cells?.[colNum];
                    if (cell) {
                        realCols++;
                    }
                    const cellText = S25Util.toStr(cell?.text);
                    rowData.cells.push({ text: cellText });
                    // Check if cell has non-empty content after trimming
                    if (cellText && cellText.trim().length > 0) {
                        hasNonEmptyCell = true;
                    }
                }
                // Stop processing if row has no cells OR if all cells are empty after trimming
                if (realCols <= 0 || !hasNonEmptyCell) {
                    break;
                }
                rows.push(rowData);
            }
        }
        return rows;
    };

    handleScrollLock = () => {
        if (this.scrollLocksHandled) {
            return;
        }
        this.scrollLocksHandled = true;

        this.spreadsheetEl.nativeElement.addEventListener("mouseover", () => {
            ScrollLockService.lockBodyScroll();
        });

        this.spreadsheetEl.nativeElement.addEventListener("mouseout", (evt) => {
            if (!this.isEventOverlappingElement(evt, this.spreadsheetEl.nativeElement)) {
                ScrollLockService.unlockBodyScroll();
            }
        });

        document.addEventListener("wheel", this.wheelListener, { passive: false });
    };

    wheelListener = (event: WheelEvent) => {
        if ((event.target as HTMLElement)?.classList?.contains("x-spreadsheet-overlayer")) {
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                event.preventDefault();

                const currentScroll = this.spreadsheet.sheet.horizontalScrollbar.scroll();
                const newLeftPosition = currentScroll.left + event.deltaX;

                this.spreadsheet.sheet.horizontalScrollbar.moveFn(newLeftPosition);
                this.spreadsheet.sheet.horizontalScrollbar.move({ left: newLeftPosition });
            }
        }
    };

    isEventOverlappingElement = (event: MouseEvent, element: HTMLElement) => {
        const rect = element.getBoundingClientRect();
        return (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
        );
    };

    async ngOnInit() {
        this.XLSX = await SpreadSheetService.XLSX();
        this.init = true;
        this.cd.detectChanges();
    }

    ngOnDestroy() {
        ScrollLockService.unlockBodyScroll();
        document.removeEventListener("wheel", this.wheelListener);
    }
}
